import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { featureFlags } from '../featureFlags';
import AdoptedOfficialList from './components/dashboard/AdoptedOfficialList';
import WhatChangedRail from './components/dashboard/WhatChangedRail';
import WatchlistSummaryStrip from './components/dashboard/WatchlistSummaryStrip';
import ReceiptCard from './components/share/ReceiptCard';
import type { ReceiptMoment } from './lib/share';
import { getShareableChangedAdoptions } from './lib/watchlistShare';

interface Adoption {
  id: string;
  adoptedAt: number;
  behavingStatus?: string;
  changedSinceLastVisit?: boolean;
  latestReceiptMoment?: ReceiptMoment | null;
  lastSeenScore?: number | null;
  lastIntelAt?: number | null;
  lastDashboardViewedAt?: number | null;
  notificationPreferences: any;
  official: {
    id: string;
    name: string;
    photoUrl: string;
    personType: string;
    officeName: string;
    officeLevel: string;
    state: string;
    party: string;
    accountabilityScore: number | null;
    adoptionCount: number;
  };
}

interface Notification {
  id: string;
  notificationType: string;
  title: string;
  message: string;
  isRead: boolean;
  createdAt: number;
  relatedOfficial: { id: string; name: string; photoUrl: string } | null;
}

interface OfficialSuggestion {
  id: string;
  name: string;
  photo_url: string;
  office_name: string;
  state: string;
  party: string;
  accountability_score: number | null;
  adoption_count: number;
}

interface SinceLastVisitSummary {
  quiet: boolean;
  changedAdoptionsCount: number;
  newNotificationsCount: number;
  unreadNotificationsCount: number;
  lastDashboardViewedAt: number | null;
  generatedAt: number;
}

type DashboardAdoptionsResponse = {
  adoptions?: Adoption[];
  sinceLastVisit?: SinceLastVisitSummary | null;
};
type DashboardNotificationsResponse = { notifications?: Notification[] };
type DashboardSuggestionsResponse = {
  officials?: OfficialSuggestion[];
  results?: OfficialSuggestion[];
  total?: number;
};
type DashboardReferralResponse = { referralCode?: string; referredCount?: number };
type DashboardReferralBoardResponse = { leaders?: Array<{ userId?: string }> };

function behavingBadgeClass(status?: string): string {
  switch (status) {
    case 'BEHAVING':
      return 'bg-green-500';
    case 'UP_TO_SOMETHING':
      return 'bg-gold-500';
    case 'ACTING_UP':
      return 'bg-red-500';
    case 'NO_RECENT_INTEL':
      return 'bg-navy-400';
    case 'NEW':
      return 'bg-gold-500';
    default:
      return 'bg-navy-300';
  }
}

function behavingLabel(status?: string): string {
  switch (status) {
    case 'BEHAVING':
      return 'Behaving';
    case 'UP_TO_SOMETHING':
      return 'Up To Something';
    case 'ACTING_UP':
      return 'Acting Up';
    case 'NO_RECENT_INTEL':
      return 'No Recent Intel';
    case 'NEW':
      return 'New';
    default:
      return 'Unknown';
  }
}

function scoreColor(score: number | null): string {
  if (score === null) return 'bg-navy-400';
  if (score >= 70) return 'bg-red-500';
  if (score >= 40) return 'bg-gold-500';
  return 'bg-green-500';
}

export default function DashboardPage() {
  const { token, isAuthenticated, isLoading, user } = useAuth();
  const navigate = useNavigate();

  const [adoptions, setAdoptions] = useState<Adoption[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [suggestions, setSuggestions] = useState<OfficialSuggestion[]>([]);
  const [totalOfficials, setTotalOfficials] = useState(0);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState('');
  const [openPrefsId, setOpenPrefsId] = useState<string | null>(null);
  const [sinceLastVisit, setSinceLastVisit] = useState<SinceLastVisitSummary | null>(null);
  const [referralCode, setReferralCode] = useState('');
  const [referredCount, setReferredCount] = useState(0);
  const [referralRank, setReferralRank] = useState<number | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle');

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    void fetchDashboard();
  }, [isAuthenticated, isLoading, navigate]);

  const fetchDashboard = async () => {
    if (!token) return;
    setLoadingData(true);
    setError('');
    const headers = { Authorization: `Bearer ${token}` };
    let shouldCheckpoint = false;

    try {
      const [adoptionsRes, notifRes, suggestionsRes, referralRes, referralBoardRes] = await Promise.all([
        fetch('/api/adoptions/me', { headers }),
        fetch('/api/notifications/me?limit=5', { headers }),
        fetch('/api/officials?sort=score_asc&limit=3'),
        fetch('/api/auth/referral-code', { headers }),
        fetch('/api/adoptions/referral-leaderboard?limit=50'),
      ]);

      if (adoptionsRes.ok) {
        const data = (await adoptionsRes.json()) as DashboardAdoptionsResponse;
        setAdoptions(data.adoptions || []);
        setSinceLastVisit(data.sinceLastVisit || null);
      }

      if (notifRes.ok) {
        const data = (await notifRes.json()) as DashboardNotificationsResponse;
        setNotifications(data.notifications || []);
      }

      if (suggestionsRes.ok) {
        const data = (await suggestionsRes.json()) as DashboardSuggestionsResponse;
        const officials = data.officials || data.results || [];
        setSuggestions(officials);
        setTotalOfficials(data.total || 0);
      }

      if (referralRes.ok) {
        const data = (await referralRes.json()) as DashboardReferralResponse;
        setReferralCode(data.referralCode || '');
        setReferredCount(data.referredCount || 0);
      }

      if (referralBoardRes.ok) {
        const data = (await referralBoardRes.json()) as DashboardReferralBoardResponse;
        const leaders = data.leaders || [];
        const byId = user?.id ? leaders.findIndex((leader) => leader.userId === user.id) : -1;
        setReferralRank(byId !== -1 ? byId + 1 : null);
      }

      shouldCheckpoint = adoptionsRes.ok && notifRes.ok;
    } catch (err: any) {
      console.error('Dashboard fetch error:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoadingData(false);

      if (featureFlags.watchlistHomeV2 && token && shouldCheckpoint) {
        void Promise.allSettled([
          fetch('/api/adoptions/me/checkpoint', { method: 'POST', headers }),
          fetch('/api/notifications/me/checkpoint', { method: 'POST', headers }),
        ]);
      }
    }
  };

  const handleCopyLink = async () => {
    if (!referralCode) return;
    const shareUrl = `https://adoptacoward.com/?ref=${referralCode}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopyState('copied');
    } catch {
      setCopyState('error');
    } finally {
      window.setTimeout(() => setCopyState('idle'), 2200);
    }
  };

  const changedAdoptions = useMemo(
    () => adoptions.filter((adoption) => adoption.changedSinceLastVisit),
    [adoptions],
  );
  const shareableChangedAdoptions = useMemo(
    () => getShareableChangedAdoptions(adoptions).slice(0, 3),
    [adoptions],
  );

  if (isLoading || loadingData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 text-gold-500 mx-auto mb-4" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <p className="text-sm font-black uppercase tracking-widest text-navy-500 dark:text-cream-300">
            Loading Watchlist
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="glass-panel max-w-md w-full rounded-lg p-8 text-center shadow-xl">
          <h1 className="mb-2 text-2xl font-black uppercase tracking-widest text-navy-900 dark:text-cream-50">
            Error
          </h1>
          <p className="mb-4 text-navy-500 dark:text-cream-300">{error}</p>
          <button
            onClick={() => void fetchDashboard()}
            className="rounded-lg bg-navy-700 px-6 py-2 text-sm font-black uppercase tracking-widest text-cream-50 transition hover:bg-navy-800"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="mb-1 text-3xl font-black uppercase tracking-widest text-navy-900 dark:text-cream-50">
            {featureFlags.watchlistHomeV2 ? 'Watchlist Home' : 'Dashboard'}
          </h1>
          <p className="text-sm text-navy-400 dark:text-navy-300">
            Welcome back, {user?.displayName || user?.username}
          </p>
        </div>

        {featureFlags.watchlistHomeV2 && (
          <WatchlistSummaryStrip
            sinceLastVisit={sinceLastVisit}
            adoptionsCount={adoptions.length}
            totalOfficials={totalOfficials}
            unreadAlerts={notifications.filter((notification) => !notification.isRead).length}
            role={user?.role}
          />
        )}

        <div className="grid gap-8 lg:grid-cols-[1.15fr_0.85fr]">
          <WhatChangedRail
            changedAdoptions={changedAdoptions}
            newNotificationsCount={sinceLastVisit?.newNotificationsCount || 0}
            totalWatched={adoptions.length}
            behavingBadgeClass={behavingBadgeClass}
            behavingLabel={behavingLabel}
          />
          <AdoptedOfficialList
            adoptions={adoptions}
            openPrefsId={openPrefsId}
            setOpenPrefsId={setOpenPrefsId}
            behavingBadgeClass={behavingBadgeClass}
            behavingLabel={behavingLabel}
          />
        </div>

        <div className="mt-8 grid gap-8 lg:grid-cols-[0.9fr_1.1fr]">
          <section>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-gold-600 dark:text-gold-400 mb-1">
                  Controls
                </p>
                <h2 className="text-lg font-black uppercase tracking-widest text-navy-900 dark:text-cream-50">
                  Digest And Alert Controls
                </h2>
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-1">
              <div className="glass-panel rounded-lg p-5">
                <p className="text-[10px] font-black uppercase tracking-widest text-gold-600 dark:text-gold-400 mb-2">
                  Alert Routing
                </p>
                <h3 className="text-xl font-black uppercase tracking-tight text-navy-900 dark:text-cream-50 mb-2">
                  Tune your watchlist signal
                </h3>
                <p className="text-sm text-navy-500 dark:text-cream-300 mb-4">
                  Review your latest alerts and adjust per-official preferences directly from the watchlist roster.
                </p>
                <Link
                  to="/notifications"
                  className="inline-flex items-center justify-center rounded-lg bg-navy-700 px-4 py-2 text-xs font-black uppercase tracking-widest text-cream-50 transition hover:bg-navy-800"
                >
                  Open Alerts
                </Link>
              </div>
              <div className="glass-panel rounded-lg p-5">
                <p className="text-[10px] font-black uppercase tracking-widest text-gold-600 dark:text-gold-400 mb-2">
                  Weekly Digest
                </p>
                <h3 className="text-xl font-black uppercase tracking-tight text-navy-900 dark:text-cream-50 mb-2">
                  Keep a weekly accountability brief
                </h3>
                <p className="text-sm text-navy-500 dark:text-cream-300 mb-4">
                  Email digest signup still lives on the homepage, but this watchlist home now keeps alerts and weekly-review behavior aligned.
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center rounded-lg border border-navy-300 px-4 py-2 text-xs font-black uppercase tracking-widest text-navy-700 transition hover:border-gold-500 hover:text-gold-700 dark:border-navy-600 dark:text-cream-200 dark:hover:border-gold-400 dark:hover:text-gold-300"
                >
                  Open Homepage Signup
                </Link>
              </div>
            </div>
          </section>

          <section>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-gold-600 dark:text-gold-400 mb-1">
                  Alerts Feed
                </p>
                <h2 className="text-lg font-black uppercase tracking-widest text-navy-900 dark:text-cream-50">
                  Recent Alerts
                </h2>
              </div>
              <Link
                to="/notifications"
                className="text-xs font-black uppercase tracking-widest text-gold-600 hover:underline dark:text-gold-400"
              >
                View All
              </Link>
            </div>
            {notifications.length === 0 ? (
              <div className="glass-panel rounded-lg p-8 text-center">
                <p className="text-sm text-navy-500 dark:text-cream-300">No recent alerts.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`glass-panel rounded-lg p-4 ${!notification.isRead ? 'border-l-4 border-l-gold-500' : ''}`}
                  >
                    <div className="mb-1 flex items-start justify-between gap-2">
                      <p className="text-sm font-bold text-navy-900 dark:text-cream-50">
                        {notification.title}
                      </p>
                      <span className="shrink-0 text-xs text-navy-400 dark:text-navy-300">
                        {new Date(notification.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-xs text-navy-500 dark:text-cream-300">{notification.message}</p>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {featureFlags.receiptSharing && (
          <section className="mt-8">
            <div className="grid gap-6 xl:grid-cols-[1.25fr_0.75fr]">
              <div className="glass-panel rounded-lg p-6">
                <div className="flex items-start justify-between gap-4 mb-5">
                  <div>
                    <p className="mb-1 text-[10px] font-black uppercase tracking-widest text-gold-600 dark:text-gold-400">
                      Share Receipts
                    </p>
                    <h2 className="text-lg font-black uppercase tracking-widest text-navy-900 dark:text-cream-50">
                      Watchlist Follow-Through You Can Prove
                    </h2>
                    <p className="mt-2 max-w-2xl text-xs text-navy-400 dark:text-navy-300">
                      Share the newest source-backed movement from your adopted officials. Each card links straight to the evidence anchor on the public dossier.
                    </p>
                  </div>
                  <Link
                    to="/officials?sort=updated_desc"
                    className="shrink-0 text-xs font-black uppercase tracking-widest text-gold-600 hover:underline dark:text-gold-400"
                  >
                    Browse Officials
                  </Link>
                </div>

                {shareableChangedAdoptions.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-cream-300 bg-cream-50/80 p-6 dark:border-navy-600 dark:bg-navy-900/30">
                    <p className="text-sm font-black uppercase tracking-widest text-navy-900 dark:text-cream-50">
                      No source-backed receipts to share yet.
                    </p>
                    <p className="mt-2 text-sm text-navy-500 dark:text-cream-300">
                      As new accountability records or public activity land for your watchlist, the freshest receipts will appear here ready to share.
                    </p>
                  </div>
                ) : (
                  <div className="grid gap-4 xl:grid-cols-2">
                    {shareableChangedAdoptions.map((adoption) => (
                      <ReceiptCard
                        key={`${adoption.id}-${adoption.latestReceiptMoment.id}`}
                        officialId={adoption.official.id}
                        officialName={adoption.official.name}
                        officeName={adoption.official.officeName}
                        moment={adoption.latestReceiptMoment}
                        shareEnabled
                        compact
                        anchorable={false}
                      />
                    ))}
                  </div>
                )}
              </div>

              <div className="glass-panel rounded-lg p-6">
                <p className="mb-1 text-[10px] font-black uppercase tracking-widest text-gold-600 dark:text-gold-400">
                  Referral Loop
                </p>
                <h2 className="text-lg font-black uppercase tracking-widest text-navy-900 dark:text-cream-50">
                  Bring More Eyes
                </h2>
                <p className="mt-2 text-xs text-navy-400 dark:text-navy-300">
                  Pair evidence-backed receipts with your invite link to grow the accountability network around the officials you watch.
                </p>

                <div className="mt-5 grid gap-4 sm:grid-cols-3 xl:grid-cols-1">
                  <div className="rounded-lg bg-cream-100 p-4 dark:bg-navy-800/60">
                    <p className="mb-1 text-[10px] font-black uppercase tracking-widest text-gold-600 dark:text-gold-400">
                      Referral Code
                    </p>
                    <p className="text-2xl font-black tracking-wider text-navy-900 dark:text-cream-50">
                      {referralCode || '—'}
                    </p>
                  </div>
                  <div className="rounded-lg bg-cream-100 p-4 dark:bg-navy-800/60">
                    <p className="mb-1 text-[10px] font-black uppercase tracking-widest text-gold-600 dark:text-gold-400">
                      Citizens Recruited
                    </p>
                    <p className="text-2xl font-black text-navy-900 dark:text-cream-50">{referredCount}</p>
                  </div>
                  <div className="rounded-lg bg-cream-100 p-4 dark:bg-navy-800/60">
                    <p className="mb-1 text-[10px] font-black uppercase tracking-widest text-gold-600 dark:text-gold-400">
                      Recruiter Rank
                    </p>
                    <p className="text-2xl font-black text-navy-900 dark:text-cream-50">
                      {referralRank !== null ? `#${referralRank}` : '—'}
                    </p>
                  </div>
                </div>

                {referralCode && (
                  <div className="mt-5 flex flex-col items-stretch gap-3">
                    <div className="min-w-0 flex-grow rounded-lg border border-cream-300 bg-cream-100 px-4 py-3 dark:border-navy-700 dark:bg-navy-800/60">
                      <span className="mr-3 block text-[10px] font-black uppercase tracking-widest text-navy-400 dark:text-navy-300">
                        Link
                      </span>
                      <span className="mt-1 block truncate font-mono text-sm text-navy-700 dark:text-cream-200">
                        https://adoptacoward.com/?ref={referralCode}
                      </span>
                    </div>
                    <button
                      onClick={handleCopyLink}
                      className="rounded-lg bg-navy-700 px-5 py-3 text-xs font-black uppercase tracking-widest text-cream-50 transition hover:bg-navy-800 dark:bg-gold-500 dark:text-navy-900 dark:hover:bg-gold-400"
                    >
                      {copyState === 'copied'
                        ? 'Copied'
                        : copyState === 'error'
                          ? 'Failed'
                          : 'Copy Invite Link'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        <section className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-black uppercase tracking-widest text-navy-900 dark:text-cream-50">
              Suggested Adoptions
            </h2>
            <Link
              to="/officials?sort=score_asc"
              className="text-xs font-black uppercase tracking-widest text-gold-600 hover:underline dark:text-gold-400"
            >
              View All
            </Link>
          </div>

          {suggestions.length === 0 ? (
            <div className="glass-panel rounded-lg p-8 text-center">
              <p className="text-sm text-navy-500 dark:text-cream-300">
                No suggestions available at this time.
              </p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-3">
              {suggestions.map((official) => (
                <Link
                  key={official.id}
                  to={`/officials/${official.id}`}
                  className="glass-panel rounded-lg p-5 transition hover:border-gold-400 group"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="h-10 w-10 shrink-0 rounded-full bg-navy-100 dark:bg-navy-900 overflow-hidden">
                      {official.photo_url ? (
                        <img src={official.photo_url} alt={official.name} className="h-10 w-10 object-cover" />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center text-xs font-black uppercase text-navy-500 dark:text-cream-300">
                          {official.name.slice(0, 2)}
                        </div>
                      )}
                    </div>
                    <p className="truncate text-sm font-bold text-navy-900 transition group-hover:text-gold-600 dark:text-cream-50 dark:group-hover:text-gold-400">
                      {official.name}
                    </p>
                  </div>
                  <p className="mb-3 text-xs text-navy-400 dark:text-navy-300">
                    {official.office_name}
                    {official.state ? ` · ${official.state}` : ''}
                  </p>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs font-black uppercase tracking-widest text-navy-400 dark:text-navy-300">
                      Spine Score
                    </span>
                    <div className="flex items-center gap-2">
                      {official.accountability_score !== null && (
                        <div className="h-2 w-20 rounded-full bg-navy-100 dark:bg-navy-900">
                          <div
                            className={`h-2 rounded-full ${scoreColor(official.accountability_score)}`}
                            style={{ width: `${official.accountability_score}%` }}
                          />
                        </div>
                      )}
                      <span className="text-sm font-black text-navy-900 dark:text-cream-50">
                        {official.accountability_score ?? '—'}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
